import { ChannelData } from "@dashboard/channels/utils";
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid, { GetCellContentOpts } from "@dashboard/components/Datagrid/Datagrid";
import { DatagridChangeOpts } from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import {
  AttributeInputTypeEnum,
  ProductDetailsVariantFragment,
  ProductFragment,
  RefreshLimitsQuery,
  useWarehouseListQuery,
} from "@dashboard/graphql";
import useStateFromProps from "@dashboard/hooks/useStateFromProps";
import EditIcon from "@dashboard/icons/Edit";
import { buttonMessages } from "@dashboard/intl";
import { ProductVariantListError } from "@dashboard/products/views/ProductUpdate/handlers/errors";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { reconstructSkuOnEdit } from "@dashboard/utils/sku";
import { Item } from "@glideapps/glide-data-grid";
import { Button, Option } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProductVariantsHeader } from "./components/ProductVariantsHeader";
import {
  useAttributesAdapter,
  useChannelAdapter,
  useChannelAvailabilityAdapter,
  useWarehouseAdapter,
  variantsStaticColumnsAdapter,
} from "./datagrid";
import messages from "./messages";
import { getData, getError } from "./utils";

interface ProductVariantsProps {
  channels: ChannelData[];
  errors: ProductVariantListError[];
  limits: RefreshLimitsQuery["shop"]["limits"];
  variantAttributes: ProductFragment["productType"]["variantAttributes"];
  variants: ProductDetailsVariantFragment[];
  productName: string;
  productId: string;
  defaultVariantId: string;
  onAttributeValuesSearch: (id: string, query: string) => Promise<Option[]>;
  onChange: (data: DatagridChangeOpts) => void;
  onRowClick: (id: string) => void;
}

export const ProductVariants = ({
  channels,
  errors,
  variants,
  variantAttributes,
  productName,
  productId,
  defaultVariantId,
  onAttributeValuesSearch,
  onChange,
  onRowClick,
}: ProductVariantsProps) => {
  const intl = useIntl();

  // https://github.com/saleor/saleor-dashboard/issues/4165
  const { data: warehousesData } = useWarehouseListQuery({
    variables: {
      first: 50,
    },
  });
  const warehouses = mapEdgesToItems(warehousesData?.warehouses);

  // Normally this should be in LS handled by useListSettings hook
  // https://github.com/saleor/saleor-dashboard/issues/4164

  const initialSettings = React.useMemo(
    () =>
      channels && warehouses && variantAttributes
        ? [
            "name",
            "sku",
            ...channels.flatMap(channel => [
              `availableInChannel:${channel.id}`,
              `channel:${channel.id}`,
            ]),
            ...warehouses.map(warehouse => `warehouse:${warehouse.id}`),
            ...(variantAttributes
              ?.filter(
                attribute =>
                  attribute.inputType === AttributeInputTypeEnum.DROPDOWN ||
                  attribute.inputType === AttributeInputTypeEnum.PLAIN_TEXT,
              )
              .map(attribute => `attribute:${attribute.id}`) ?? []),
          ]
        : undefined,
    [channels, variantAttributes, warehouses],
  );
  const [columnSettings, setColumnSettings] = useStateFromProps<string[] | undefined>(
    initialSettings,
  );

  React.useEffect(() => {
    if (columnSettings) {
      handlers.onResetDynamicToInitial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSettings]);

  const handleColumnChange = React.useCallback(
    (picked: string[]) => {
      setColumnSettings(picked);
    },
    [setColumnSettings],
  );

  // const limitReached = isLimitReached(limits, "productVariants");

  const channelCategory = useChannelAdapter({
    intl,
    listings: channels,
    selectedColumns: columnSettings,
  });
  const availabilityCategory = useChannelAvailabilityAdapter({
    intl,
    listings: channels,
    selectedColumns: columnSettings,
  });
  const attributeCategory = useAttributesAdapter({
    intl,
    selectedColumns: columnSettings,
    attributes: variantAttributes,
  });
  const warehouseCategory = useWarehouseAdapter({
    selectedColumns: columnSettings,
    intl,
    warehouses,
  });
  const memoizedStaticColumns = React.useMemo(() => variantsStaticColumnsAdapter(intl), [intl]);
  const {
    handlers,
    columnCategories,
    visibleColumns,
    staticColumns,
    dynamicColumns,
    selectedColumns,
    recentlyAddedColumn,
  } = useColumns({
    gridName: "variants_v2",
    staticColumns: memoizedStaticColumns,
    columnCategories: [channelCategory, availabilityCategory, attributeCategory, warehouseCategory],
    selectedColumns: columnSettings ?? [],
    onSave: handleColumnChange,
  });
  const getVisibleVariant = React.useCallback(
    (row: number, removed: number[] = []) =>
      variants.filter((_, index) => !removed.includes(index))[row],
    [variants],
  );
  const getCellContent = React.useCallback(
    ([column, row]: Item, opts: GetCellContentOpts) =>
      getData({
        availableColumns: visibleColumns,
        column,
        row,
        channels,
        variants,
        searchAttributeValues: onAttributeValuesSearch,
        defaultVariantId: defaultVariantId,
        ...opts,
      }),
    [channels, visibleColumns, onAttributeValuesSearch, variants, defaultVariantId],
  );
  const getCellError = React.useCallback(
    ([column, row]: Item, opts: GetCellContentOpts) =>
      getError(errors, {
        availableColumns: visibleColumns,
        column,
        row,
        channels,
        variants,
        searchAttributeValues: onAttributeValuesSearch,
        defaultVariantId: defaultVariantId,
        ...opts,
      }),
    [errors, visibleColumns, channels, variants, onAttributeValuesSearch, defaultVariantId],
  );

  const handleChange = React.useCallback(
    (data: DatagridChangeOpts) => {
      const { currentUpdate } = data;

      // Sanitize SKU suffix and reconstruct the full composite SKU using the
      // store's separator (see reconstructSkuOnEdit).
      if (currentUpdate?.column === "sku" && typeof currentUpdate.data === "string") {
        const variant = getVisibleVariant(currentUpdate.row, data.removed);
        const isDefaultVariant = variant?.id === defaultVariantId;

        if (isDefaultVariant) {
          // The default/base variant's own SKU is never prefixed.
          currentUpdate.data = currentUpdate.data.replace(/[^a-zA-Z0-9\-.]/g, "");
        } else {
          const originalSku = variant?.sku ?? "";
          // Splitting the original SKU at the first separator occurrence
          // is unsafe whenever the base SKU can itself contain the
          // separator character (e.g. base "AB-12" with separator "-"),
          // so always pass the base variant's own known SKU explicitly.
          const explicitPrefix = variants.find(v => v.id === defaultVariantId)?.sku ?? "";

          currentUpdate.data = reconstructSkuOnEdit(
            originalSku,
            currentUpdate.data,
            undefined,
            explicitPrefix,
          );
        }
      }

      onChange(data);
    },
    [onChange, getVisibleVariant, defaultVariantId, variants],
  );

  return (
    <Datagrid
      fillHandle={true}
      renderHeader={props => (
        <ProductVariantsHeader
          {...props}
          productId={productId}
          productName={productName}
          variantCount={variants?.length ?? 0}
        />
      )}
      availableColumns={visibleColumns}
      emptyText={intl.formatMessage(messages.empty)}
      getCellContent={getCellContent}
      getCellError={getCellError}
      menuItems={index => [
        {
          label: "Edit Variant",
          onSelect: () => onRowClick(variants[index].id),
          Icon: <EditIcon />,
        },
      ]}
      rows={variants?.length ?? 0}
      selectionActions={(indexes, { removeRows }) => (
        <Button
          data-test-id="bulk-delete-button"
          variant="tertiary"
          onClick={() => removeRows(indexes)}
        >
          <FormattedMessage {...buttonMessages.delete} />
        </Button>
      )}
      onColumnResize={handlers.onResize}
      onColumnMoved={handlers.onMove}
      renderColumnPicker={() => (
        <ColumnPicker
          staticColumns={staticColumns}
          dynamicColumns={dynamicColumns}
          selectedColumns={selectedColumns}
          columnCategories={columnCategories}
          onToggle={handlers.onToggle}
          side="left"
        />
      )}
      onChange={handleChange}
      recentlyAddedColumn={recentlyAddedColumn}
    />
  );
};

ProductVariants.displayName = "ProductVariants";
export default ProductVariants;
